import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { usePermission } from '../../hooks/usePermission';
import { HOME_PERMISSIONS } from '../../constants/permissions.constants';
import { HOME_PATH } from '../../constants/routes.constants';
import { selectPermissionsLoaded } from '../../redux/slices/auth.slice';
import type { PermissionString } from '../../types/permission.types';

type PermissionGateProps = {
  permission: PermissionString;
  children: React.ReactElement;
  /** When denied, go here instead of home */
  fallback?: string;
};

/** Home shell without KPIs: title only, no denial copy. */
const EmptyControlPanel: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      Panel de Control
    </Typography>
  </Box>
);

/**
 * Renders children only if the user has the permission; otherwise redirects home.
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  children,
  fallback = HOME_PATH,
}) => {
  const allowed = usePermission(permission);
  const permissionsLoaded = useSelector(selectPermissionsLoaded);

  // Wait until permissions are known so we do not redirect on a stale empty list.
  if (!allowed && !permissionsLoaded) {
    return null;
  }

  if (!allowed) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

/**
 * Index route (`/dashboard`):
 * - With home permission: real dashboard (never flash a denial — if perms already
 *   known from persist, render immediately).
 * - Without home permission: same home shell, empty, only "Panel de Control".
 *   Does not open another menu.
 */
export const DashboardHomeGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const canViewHome = usePermission(HOME_PERMISSIONS.VIEW);
  const permissionsLoaded = useSelector(selectPermissionsLoaded);

  if (canViewHome) {
    return children;
  }

  // Still resolving permissions — blank content, no denial message.
  if (!permissionsLoaded) {
    return null;
  }

  return <EmptyControlPanel />;
};
